import type { SyncedRow } from "../types/base.js";

/**
 * Konfliktregel: Last-Write-Wins über `client_updated_at`.
 *
 * Warum nicht `updated_at`: Ein Gerät kann Stunden offline sein. Die Serverzeit
 * sagt dann nur, wann die Zeile hochgeladen wurde, nicht wann sie gemeint war.
 * Bei exakt gleicher Clientzeit entscheidet die Serverzeit, danach die ID, damit
 * zwei Geräte unabhängig voneinander dasselbe Ergebnis errechnen.
 *
 * Grabsteine gewinnen nicht automatisch: Wer nach dem Löschen wieder anfasst,
 * hat die jüngere Clientzeit und holt die Zeile zurück.
 */
export function resolveConflict<T extends SyncedRow>(local: T, remote: T): T {
  const byClient = compare(local.client_updated_at, remote.client_updated_at);
  if (byClient !== 0) return byClient > 0 ? local : remote;

  const byServer = compare(local.updated_at, remote.updated_at);
  if (byServer !== 0) return byServer > 0 ? local : remote;

  return local.id <= remote.id ? local : remote;
}

function compare(a: string, b: string): number {
  const left = Date.parse(a);
  const right = Date.parse(b);
  if (Number.isNaN(left) && Number.isNaN(right)) return 0;
  if (Number.isNaN(left)) return -1;
  if (Number.isNaN(right)) return 1;
  return left === right ? 0 : left > right ? 1 : -1;
}

/**
 * Wendet eingehende Zeilen auf den lokalen Bestand an. Ergebnis ist die Liste
 * der Zeilen, die tatsächlich geschrieben werden müssen.
 */
export function mergeRows<T extends SyncedRow>(local: readonly T[], remote: readonly T[]): T[] {
  const byId = new Map(local.map((row) => [row.id, row]));
  const writes: T[] = [];

  for (const incoming of remote) {
    const existing = byId.get(incoming.id);
    if (!existing) {
      writes.push(incoming);
      continue;
    }
    const winner = resolveConflict(existing, incoming);
    if (winner !== existing) writes.push(winner);
  }

  return writes;
}

/** Eine Zeile gilt als gelöscht, sobald ein Grabstein gesetzt ist. */
export function isDeleted(row: SyncedRow): boolean {
  return row.deleted_at !== null;
}
